/**
 * Config notifications Admin — Lot 7 (rappels deadline + journal récent).
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  configApi,
  ApiRequestError,
  type NotificationDto,
  type NotificationSettingsDto,
  type ReminderRunResult,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

export function NotificationConfigPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<NotificationSettingsDto | null>(null);
  const [recent, setRecent] = useState<NotificationDto[]>([]);
  const [offsetsText, setOffsetsText] = useState("14, 3, 0");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await configApi.getNotifications();
      setSettings(res.settings);
      setRecent(res.recent);
      setOffsetsText(res.settings.reminderOffsetsDays.join(", "));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("notif_load_failed"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave() {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const offsets = offsetsText
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n));
      const res = await configApi.patchNotifications({ reminderOffsetsDays: offsets });
      setSettings(res.settings);
      setOffsetsText(res.settings.reminderOffsetsDays.join(", "));
      setInfo(t("notif_saved"));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("notif_save_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onRun() {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await configApi.runReminders();
      const r: ReminderRunResult = res.result;
      setInfo(
        t("notif_run_result", {
          sent: r.sent,
          skipped: r.skipped,
          failed: r.failed,
          candidates: r.candidates,
        })
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("notif_run_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <PageHeader title={t("nav_notifications")} subtitle={t("notif_sub")} />

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}
      {info ? (
        <div className="sm-alert sm-alert-ok" role="status">
          {info}
        </div>
      ) : null}

      <div className="sm-card sm-card-pad" style={{ marginBottom: 16, maxWidth: 520 }}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>{t("notif_offsets")}</h2>
        <p className="sm-muted" style={{ fontSize: 13 }}>
          {t("notif_offsets_hint")}
        </p>
        <label htmlFor="notif-offsets" className="sm-muted" style={{ fontSize: 13 }}>
          {t("notif_offsets_label")}
        </label>
        <input
          id="notif-offsets"
          className="sm-input"
          value={offsetsText}
          onChange={(e) => setOffsetsText(e.target.value)}
          style={{ marginTop: 4, marginBottom: 12 }}
        />
        {settings ? (
          <p className="sm-muted" style={{ fontSize: 13 }}>
            {t("notif_mail_mode")}: <strong>{settings.mailMode}</strong>
            {" · "}
            {settings.smtpFrom}
            {settings.lastReminderRunDate
              ? ` · ${t("notif_last_run")}: ${settings.lastReminderRunDate}`
              : ""}
          </p>
        ) : null}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <button
            type="button"
            className="sm-btn sm-btn-primary"
            disabled={busy}
            onClick={() => void onSave()}
          >
            {t("save")}
          </button>
          <button
            type="button"
            className="sm-btn sm-btn-secondary"
            disabled={busy}
            onClick={() => void onRun()}
          >
            {t("notif_run_now")}
          </button>
        </div>
      </div>

      <div className="sm-card">
        <div className="sm-card-pad" style={{ paddingBottom: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>{t("notif_recent")}</h2>
        </div>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("status")}</th>
                <th>{t("notif_type")}</th>
                <th>{t("notif_recipient")}</th>
                <th>{t("notif_sent_at")}</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={4} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                recent.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <span className="sm-badge-pill sm-badge-gray">{n.status ?? "—"}</span>
                    </td>
                    <td>{n.type}</td>
                    <td className="sm-muted" style={{ fontSize: 13 }}>
                      {n.recipientEmail ?? n.recipientId.slice(0, 8)}
                    </td>
                    <td className="sm-muted" style={{ fontSize: 13 }}>
                      {n.sentAt ? new Date(n.sentAt).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
